<template>
  <div class="content-payment">
    <el-card class="payment-card">
      <div class="order-header">
        <h2>Order Details</h2>
        <div class="order-status" :class="orderInfo.status.toLowerCase()">
          {{ orderInfo.status }} PAYMENT
        </div>
      </div>

      <div class="order-content">
        <div class="content-info">
          <img 
            v-if="orderInfo.content_info?.cover_url" 
            :src="orderInfo.content_info?.cover_url" 
            class="content-cover"
            alt="Content cover"
          />
          <div class="content-details">
            <span class="content-type" :class="orderInfo.content_type">
              {{ orderInfo.content_type }}
            </span>
            <h3>{{ orderInfo.content_info?.title }}</h3>
            <p class="price">${{ orderInfo.usd_price }} per copy</p>
          </div>
        </div>

        <div class="order-details">
          <div class="detail-item">
            <span class="label">Order ID:</span>
            <span class="value">{{ orderInfo._id?.$id || '-' }}</span>
          </div>

          <div class="detail-item">
            <span class="label">Total Copies:</span>
            <span class="value">{{ orderInfo.quantity }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Total Amount:</span>
            <span class="value">${{ orderInfo.total_price }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Order Date:</span>
            <span class="value">{{ formatDate(orderInfo.order_time*1000) }}</span>
          </div>

          
        </div>

        <div class="payment-section" v-if="orderInfo.status === 'PENDING'">
          <div class="payment-method">
            <h4>Pay by Credit Card</h4>
            <el-button 
              type="primary" 
              @click="processPayment" 
              :loading="processing"
            >
              Pay Now by Credit Card
            </el-button>
          </div>

          <div class="payment-method">
            <h4>Pay by Bank Transfer</h4>

            <!-- Add new receipt details -->
          <template v-if="orderInfo.payment_receipt_url">
            <div class="detail-item">
              <span class="label">Payment Receipt Status:</span>
              <span class="value" :class="receiptStatusClass">
                {{ formatReceiptStatus(orderInfo.payment_receipt_status) }}
              </span>
            </div>
            <div class="detail-item">
              <span class="label">Upload Time:</span>
              <span class="value">{{ formatDate(orderInfo.payment_receipt_upload_time*1000) }}</span>
            </div>
            <div class="detail-item">
              <span class="label">Payment Receipt:</span>
              <span class="value">
                <a :href="orderInfo.payment_receipt_url" target="_blank">View Receipt</a>
              </span>
            </div>
          </template>
            <el-button 
              type="primary" 
              plain
              @click="downloadInvoice" 
              :loading="downloading"
            >
              Download Payment Invoice for Bank Transfer
            </el-button>
            <el-button
              type="success"
              plain
              @click="uploadReceipt"
              :loading="uploading"
              style="margin-top: 12px;"
            >
              {{ orderInfo.payment_receipt_url ? 'Re-upload Payment Receipt' : 'Upload Payment Receipt for Speedy Validation' }}
            </el-button>
          </div>
        </div>
        
        <div class="payment-section">
          <router-link to="/home/contentOrders" class="view-orders-link">
            → View My Order List
          </router-link>
        </div>
      </div>
    </el-card>
    <div class="cancel-order">
      <el-button type="text" @click="cancelOrder" :loading="cancelling" v-if="orderInfo.status === 'PENDING'">
        Cancel this order
      </el-button>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import jsPDF from 'jspdf'
import * as qiniu from "qiniu-js";

export default {
  name: 'ContentPayment',
  data() {
    return {
      orderInfo: {
        status: 'PENDING',
        content: {},
        _id: null,
        order_id: '',
        quantity: 0,
        total_price: 0,
        order_time: null
      },
      selectedPayment: 'credit_card',
      processing: false,
      downloading: false,
      sending: false,
      cancelling: false,
      uploading: false
    }
  },
  created() {
    this.fetchOrderInfo()
  },
  methods: {
    async fetchOrderInfo() {
      try {
        const response = await postRequest('getContentOrder', {
          order_id: this.$route.query.order_id
        })
        if (response.data.code === 0) {
          this.orderInfo = response.data.data
        }
      } catch (error) {
        console.error('Error fetching order info:', error)
        this.$message.error('Failed to load order information')
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleString()
    },
    async processPayment() {
      this.processing = true
      try {
        // Redirect to payment page with order ID and content type
        this.$router.push({

          path: 'payment',
          query: { 
            order_id: this.$route.query.order_id,
            content_type: this.orderInfo.content_type 
          }

        })
      } catch (error) {
        console.error('Payment error:', error)
        this.$message.error('Failed to process payment')
      } finally {
        this.processing = false
      }
    },
    async downloadInvoice() {
      this.downloading = true
      try {
        const doc = new jsPDF()
        
        // Add company logo/header
        doc.setFontSize(20)
        doc.text('Payment Invoice', 105, 20, { align: 'center' })
        
        // Add invoice details
        doc.setFontSize(12)
        doc.text(`Invoice Date: ${this.formatDate(Date.now())}`, 20, 40)
        doc.text(`Order ID: ${this.orderInfo._id?.$id || '-'}`, 20, 50)
        doc.text(`Order Date: ${this.formatDate(this.orderInfo.order_time*1000)}`, 20, 60)
        
        // Add content details
        doc.text('Item Details:', 20, 80)
        doc.text(`Title: ${this.orderInfo.content_info?.title || '-'}`, 30, 90)
        doc.text(`Type: ${this.orderInfo.content_type}`, 30, 100)
        doc.text(`Quantity: ${this.orderInfo.quantity}`, 30, 110)
        doc.text(`Price per Copy: $${this.orderInfo.usd_price}`, 30, 120)
        doc.text(`Total Amount: $${this.orderInfo.total_price}`, 30, 130)
        
        // Add footer
        doc.setFontSize(10)
        doc.text('Thank you for your business!', 105, 150, { align: 'center' })
        
        // Download the PDF
        doc.save(`invoice-${this.orderInfo._id?.$id || 'order'}.pdf`)
        this.$message.success('Invoice downloaded successfully')
      } catch (error) {
        console.error('Download error:', error)
        this.$message.error('Failed to download invoice')
      } finally {
        this.downloading = false
      }
    },
    async sendInvoice() {
      this.sending = true
      try {
        const response = await postRequest('sendInvoice', {
          order_id: this.$route.query.order_id
        })
        
        if (response.data.code === 0) {
          this.$message.success('Invoice sent successfully')
        }
      } catch (error) {
        console.error('Send error:', error)
        this.$message.error('Failed to send invoice')
      } finally {
        this.sending = false
      }
    },
    async cancelOrder() {
      try {
        // Add confirmation dialog
        await this.$confirm(
          'Are you sure you want to cancel this order?',
          'Cancel Order',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        )

        this.cancelling = true
        const response = await postRequest('cancelContentOrder', {
          order_id: this.$route.query.order_id
        })
        
        if (response.data.code === 0) {
          this.$message.success('Order cancelled successfully')
          // Refresh order info to show updated status
          await this.fetchOrderInfo()
        }
      } catch (error) {
        if (error === 'cancel') {
          // User clicked "No" in the confirmation dialog
          return
        }
        console.error('Cancel error:', error)
        this.$message.error('Failed to cancel order')
      } finally {
        this.cancelling = false
      }
    },
    async uploadReceipt() {
      try {
        // Create input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,.pdf';
        
        input.onchange = async (event) => {
          const file = event.target.files[0];
          if (!file) return;

          this.uploading = true;
          
          try {
            // Get Qiniu upload token
            const tokenResult = await postRequest('getQiniuToken');
            if (tokenResult.data.code !== 0) {
              this.$message.warning('Failed to get upload token');
              return;
            }

            const token = tokenResult.data.data;
            // Generate a unique key for the file
            const key = `receipts/${Date.now()}-${file.name}`;
            
            // Create upload observable using qiniu-js
            const observable = qiniu.upload(file, key, token, {}, {});
            
            observable.subscribe({
              next: (res) => {
                // Optional: Add progress handling here
                console.log('Upload progress:', res.total.percent);
              },
              error: (err) => {
                console.error('Upload error:', err);
                this.$message.error('Failed to upload payment receipt');
                this.uploading = false;
              },
              complete: async (res) => {
                const receiptUrl = `http://mainbucket.reachable-edu.com/${res.key}`;
                
                // Submit receipt URL to backend
                const response = await postRequest('uploadPaymentReceipt', {
                  order_id: this.$route.query.order_id,
                  receipt_url: receiptUrl
                });

                if (response.data.code === 0) {
                  this.$message.success('Payment receipt uploaded successfully');
                  await this.fetchOrderInfo();
                }
                this.uploading = false;
              }
            });
          } catch (error) {
            console.error('Upload error:', error);
            this.$message.error('Failed to upload payment receipt');
            this.uploading = false;
          }
        };

        input.click();
      } catch (error) {
        console.error('Upload error:', error);
        this.$message.error('Failed to upload payment receipt');
        this.uploading = false;
      }
    },
    formatReceiptStatus(status) {
      if (!status) return '-'
      return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  },
  computed: {
    receiptStatusClass() {
      const status = this.orderInfo.payment_receipt_status?.toLowerCase()
      return {
        'pending-review': status === 'pending_review',
        'approved': status === 'approved',
        'rejected': status === 'rejected'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content-payment {
  padding: 20px;

  .payment-card {
    max-width: 800px;
    margin: 0 auto;

    .order-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h2 {
        margin: 0;
      }

      .order-status {
        padding: 4px 12px;
        border-radius: 4px;
        font-weight: 500;

        &.pending {
          background-color: #fdf6ec;
          color: #e6a23c;
        }

        &.completed {
          background-color: #f0f9eb;
          color: #67c23a;
        }
      }
    }

    .content-info {
      display: flex;
      gap: 20px;
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid #ebeef5;

      .content-cover {
        width: 120px;
        height: 168px;
        object-fit: cover;
        border-radius: 8px;
      }

      .content-details {
        .content-type {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          margin-bottom: 8px;

          &.events {
            background-color: #e1f3d8;
            color: #67c23a;
          }

          &.posts {
            background-color: #fdf6ec;
            color: #e6a23c;
          }
        }

        h3 {
          margin: 0 0 8px;
          color: #303133;
        }

        .price {
          color: #f56c6c;
          font-weight: 500;
          margin: 0;
        }
      }
    }

    .order-details {
      margin-bottom: 24px;

      .detail-item {
        display: flex;
        margin-bottom: 12px;

        .label {
          width: 180px;
          color: #909399;
        }

        .value {
          color: #303133;
          font-weight: 500;

          &.pending-review {
            color: #e6a23c;
            background-color: #fdf6ec;
            padding: 2px 8px;
            border-radius: 4px;
            display: inline-block;
          }

          &.approved {
            color: #67c23a;
            background-color: #f0f9eb;
            padding: 2px 8px;
            border-radius: 4px;
            display: inline-block;
          }

          &.rejected {
            color: #f56c6c;
            background-color: #fef0f0;
            padding: 2px 8px;
            border-radius: 4px;
            display: inline-block;
          }

          a {
            color: #409EFF;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    .payment-section {
      .payment-method {
        margin-bottom: 24px;
        
        .detail-item {
          display: flex;
          margin-bottom: 12px;

          .label {
            width: 180px;
            color: #909399;
          }

          .value {
            color: #303133;
            font-weight: 500;

            &.pending-review {
              color: #e6a23c;
              background-color: #fdf6ec;
              padding: 2px 8px;
              border-radius: 4px;
              display: inline-block;
            }

            &.approved {
              color: #67c23a;
              background-color: #f0f9eb;
              padding: 2px 8px;
              border-radius: 4px;
              display: inline-block;
            }

            &.rejected {
              color: #f56c6c;
              background-color: #fef0f0;
              padding: 2px 8px;
              border-radius: 4px;
              display: inline-block;
            }

            a {
              color: #409EFF;
              text-decoration: none;

              &:hover {
                text-decoration: underline;
              }
            }
          }
        }

        h4 {
          margin: 0 0 12px;
          color: #909399;
          font-size: 14px;
          padding-bottom: 8px;
          border-bottom: 1px solid #ebeef5;
        }
      }

      .view-orders-link {
        display: block;
        text-align: right;
        color: #409EFF;
        text-decoration: none;
        margin-top: 16px;
        font-size: 14px;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.cancel-order {
  text-align: center;
  margin-top: 16px;

  .el-button {
    color: #909399;
    font-weight: normal;
  }
}
</style> 